import React, { useState, useEffect } from 'react';
import SigninHeader from '../auth/SigninHeader';
import SideNavigation from './SideNavigation';
import PageHeader from '../shared/PageHeader';
import axios from 'axios';

const Profile = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [user, setUser] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    mobilePhone: '',
    agencyName: '',
    jurisdictionType: '',
    roleAtAgency: '',
    emailUpdates: false,
    areasOfInterest: []
  });
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [originalFormData, setOriginalFormData] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // Separate states for current (server) and preview (temporary) images
  const [currentProfileImageUrl, setCurrentProfileImageUrl] = useState(null); // What's actually saved
  const [previewProfileImageUrl, setPreviewProfileImageUrl] = useState(null); // What user sees as preview
  const [pendingImageFile, setPendingImageFile] = useState(null); // File to upload on save
  const [imageActionType, setImageActionType] = useState(null); // 'upload', 'remove', or null
  
  const [uploadError, setUploadError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [updateError, setUpdateError] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const interestOptions = [
    { value: 'building-permits', label: 'Building Permits' },
    { value: 'zoning-regulations', label: 'Zoning Regulations' },
    { value: 'code-enforcement', label: 'Code Enforcement' },
    { value: 'planning-applications', label: 'Planning Applications' },
    { value: 'inspections', label: 'Inspections' },
    { value: 'fire-safety', label: 'Fire Safety' },
    { value: 'environmental-health', label: 'Environmental Health' },
    { value: 'business-licensing', label: 'Business Licensing' },
    { value: 'public-works', label: 'Public Works' },
    { value: 'property-assessment', label: 'Property Assessment' }
  ];

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));

    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token found');
        setUpdateError('Please sign in to view your profile');
        return;
      }

      const response = await axios.get('http://localhost:5000/api/user/profile', {
        headers: { Authorization: token }
      });

      if (response.data.success) {
        const userData = response.data.user;
        setUser(userData);
        setSelectedInterests(userData.areasOfInterest || []);
        setOriginalFormData(userData);
        
        // Set both current and preview image URLs to the same value initially
        if (userData.profile_image_path) {
          const imageUrl = `http://localhost:5000/api/user/profile-image/${userData.profile_image_path}`;
          setCurrentProfileImageUrl(imageUrl);
          setPreviewProfileImageUrl(imageUrl);
        } else {
          setCurrentProfileImageUrl(null);
          setPreviewProfileImageUrl(null);
        }
        
        // Reset image action states
        setPendingImageFile(null);
        setImageActionType(null);
      } else {
        setUpdateError('Failed to load profile data');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (error.response?.status === 401) {
        setUpdateError('Your session has expired. Please sign in again.');
      } else {
        setUpdateError('Failed to load profile data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return 'U';
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || 'U';
  };

  const handleInputChange = (field, value) => {
    setUser(prev => ({ ...prev, [field]: value }));
    setUpdateError('');
  };

  const handleInterestToggle = (value) => {
    setSelectedInterests(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
    setUpdateError('');
  };

  // Handle temporary image upload (just preview, don't save yet)
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    setUploadError('');

    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setUploadError('Image size must be less than 5MB');
      return;
    }

    // Create a preview URL for the selected file
    const previewUrl = URL.createObjectURL(file);
    
    // Update states for preview
    setPreviewProfileImageUrl(previewUrl);
    setPendingImageFile(file);
    setImageActionType('upload');
    
    // Clear file input to allow selecting the same file again if needed
    event.target.value = '';
  };

  // Handle temporary image removal (just preview, don't save yet)
  const removeProfileImagePreview = () => {
    // Revoke the object URL if it exists to prevent memory leaks
    if (previewProfileImageUrl && previewProfileImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewProfileImageUrl);
    }
    
    setPreviewProfileImageUrl(null);
    setPendingImageFile(null);
    setImageActionType(currentProfileImageUrl ? 'remove' : null);
    
    // Clear file input
    const fileInput = document.getElementById('profileImageInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Reset image preview to original
  const resetImagePreview = () => {
    // Revoke the object URL if it exists
    if (previewProfileImageUrl && previewProfileImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewProfileImageUrl);
    }
    
    setPreviewProfileImageUrl(currentProfileImageUrl);
    setPendingImageFile(null);
    setImageActionType(null);
    
    // Clear file input
    const fileInput = document.getElementById('profileImageInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Handle actual image upload/removal when saving profile
  const handleImageSave = async (token) => {
    if (imageActionType === 'upload' && pendingImageFile) {
      // Upload new image
      const formData = new FormData();
      formData.append('profileImage', pendingImageFile);

      const response = await axios.post(
        'http://localhost:5000/api/user/profile-image',
        formData,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const newImageUrl = `http://localhost:5000/api/user/profile-image/${response.data.imagePath}`;
        
        // Update current image URL and user data
        setCurrentProfileImageUrl(newImageUrl);
        setPreviewProfileImageUrl(newImageUrl);
        
        // Clean up object URL
        if (previewProfileImageUrl && previewProfileImageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewProfileImageUrl);
        }
        
        return { ...user, profile_image_path: response.data.imagePath };
      } else {
        throw new Error(response.data.message || 'Failed to upload image');
      }
    } else if (imageActionType === 'remove') {
      // Remove current image
      const response = await axios.delete(
        'http://localhost:5000/api/user/profile-image',
        { headers: { Authorization: token } }
      );

      if (response.data.success) {
        setCurrentProfileImageUrl(null);
        setPreviewProfileImageUrl(null);
        return { ...user, profile_image_path: null };
      } else {
        throw new Error(response.data.message || 'Failed to remove image');
      }
    }
    
    return user; // No image changes
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setUpdateError('');
      setUploadError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setUpdateError('Please sign in to update your profile');
        return;
      }

      let updatedUserData = user;

      // Handle image changes first
      try {
        updatedUserData = await handleImageSave(token);
      } catch (imageError) {
        console.error('Error handling image:', imageError);
        setUploadError(imageError.message || 'Failed to handle image changes');
        // Continue with profile update even if image fails
      }

      // Update profile data
      const profileData = { 
        ...updatedUserData, 
        areasOfInterest: selectedInterests 
      };

      const response = await axios.put(
        'http://localhost:5000/api/user/profile',
        profileData,
        {
          headers: { Authorization: token }
        }
      );

      if (response.data.success) {
        const finalUserData = response.data.user;
        setUser(finalUserData);
        setOriginalFormData(finalUserData);
        setSelectedInterests(finalUserData.areasOfInterest || []);
        
        // Update localStorage to sync with SigninHeader
        localStorage.setItem('user', JSON.stringify(finalUserData));
        
        // Trigger event for SigninHeader to update
        const finalImageUrl = finalUserData.profile_image_path 
          ? `http://localhost:5000/api/user/profile-image/${finalUserData.profile_image_path}`
          : null;
          
        window.dispatchEvent(new CustomEvent('userProfileUpdated', { 
          detail: { user: finalUserData, imageUrl: finalImageUrl } 
        }));
        
        // Reset image action states
        setPendingImageFile(null);
        setImageActionType(null);
        
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        setUpdateError(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      if (error.response?.status === 401) {
        setUpdateError('Your session has expired. Please sign in again.');
      } else if (error.response?.status === 409) {
        setUpdateError('Email already exists. Please use a different email.');
      } else {
        setUpdateError(error.response?.data?.message || 'Failed to update profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    setPasswordError('');
    
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (!validatePassword(passwordFormData.newPassword)) {
      setPasswordError('Password does not meet requirements');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPasswordError('Please sign in to change your password');
        return;
      }
      
      const response = await axios.put(
        'http://localhost:5000/api/user/password',
        {
          currentPassword: passwordFormData.currentPassword,
          newPassword: passwordFormData.newPassword
        },
        {
          headers: { Authorization: token }
        }
      );

      if (response.data.success) {
        setShowPasswordModal(false);
        setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError('');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        setPasswordError(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 401) {
        setPasswordError('Current password is incorrect');
      } else {
        setPasswordError(error.response?.data?.message || 'Failed to change password');
      }
    }
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return Object.values(requirements).every(Boolean);
  };

  const isFormChanged = () => {
    const currentData = { ...user, areasOfInterest: selectedInterests };
    const originalData = { ...originalFormData, areasOfInterest: originalFormData.areasOfInterest || [] };
    
    // Check if form data changed OR image changed
    const formDataChanged = JSON.stringify(currentData) !== JSON.stringify(originalData);
    const imageChanged = imageActionType !== null;
    
    return formDataChanged || imageChanged;
  };

  const handleCancel = () => {
    // Reset form data
    setUser(originalFormData);
    setSelectedInterests(originalFormData.areasOfInterest || []);
    setUpdateError('');
    setUploadError('');
    
    // Reset image preview
    resetImagePreview();
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-main-dark' : 'bg-gray-100'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-main-dark text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <SigninHeader isDarkMode={isDarkMode} isLoggedIn={true} />
      
      {/* Side Navigation */}
      <SideNavigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      {/* Page Header */}
      <PageHeader 
        title="Profile" 
        user={user} 
        isDarkMode={isDarkMode} 
        showUserInfo={true} 
      />

      {/* Main Content Container - Fixed Height with Internal Scrolling */}
      <main className="ml-20 pt-[130px] pb-8 px-8 h-screen overflow-hidden">
        <div className="h-full max-w-4xl mx-auto flex flex-col justify-center">
          {/* Messages - Outside Scrollable Area */}
          <div className="flex-shrink-0 mb-4">
            {/* Success Message */}
            {showSuccessMessage && (
              <div className="mb-4 bg-green-600 text-white p-4 rounded-lg flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Profile updated successfully!
              </div>
            )}

            {/* Error Message */}
            {updateError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg flex items-center gap-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {updateError}
              </div>
            )}

            {/* Upload Error Message */}
            {uploadError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg flex items-center gap-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {uploadError}
              </div>
            )}
          </div>

          {/* Scrollable Profile Card */}
          <div className="flex-1 overflow-hidden max-h-[calc(100vh-180px)]">
            <div className={`h-full rounded-xl shadow-lg overflow-hidden ${
              isDarkMode ? 'bg-[#1F2023] border-gray-700' : 'bg-white border-gray-200'
            } border flex flex-col`}>
              
              {/* Profile Header - Fixed */}
              <div className="flex-shrink-0 bg-gradient-to-r from-[#100E27] to-[#1a1a2e] px-8 py-6 text-center relative">
                <div className="flex items-center justify-center gap-6">
                  <div className="relative z-10">
                    <div 
                      className="w-20 h-20 bg-white/20 border-3 border-white/30 rounded-full flex items-center justify-center text-white text-2xl font-medium cursor-pointer hover:border-white/50 transition-all overflow-hidden relative"
                      onClick={() => !imageUploading && document.getElementById('profileImageInput').click()}
                    >
                      {imageUploading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      ) : previewProfileImageUrl ? (
                        <img 
                          src={previewProfileImageUrl} 
                          alt="Profile" 
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        getInitials(user.firstName, user.lastName)
                      )}
                      
                      {!imageUploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H6a2 2 0 00-2 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    {/* Hidden file input */}
                    <input
                      id="profileImageInput"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={imageUploading}
                    />
                    
                    {/* Remove image button - only show if there's a preview image */}
                    {previewProfileImageUrl && !imageUploading && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeProfileImagePreview();
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors z-20"
                        title="Remove image"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="text-xl font-semibold text-white">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-white/80 text-sm">{user.email}</div>
                    <div className="text-white/60 text-xs mt-1">
                      {imageUploading 
                        ? 'Uploading...' 
                        : `Click photo to ${previewProfileImageUrl ? 'change' : 'upload'} image`
                      }
                      {imageActionType && (
                        <div className="mt-1 text-yellow-300 text-xs">
                          {imageActionType === 'upload' ? '⚠ New image will be saved when you click "Save Changes"' : 
                           imageActionType === 'remove' ? '⚠ Image will be removed when you click "Save Changes"' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Rest of the form remains the same until the form actions */}
              <div className="flex-1 overflow-y-auto">
                <div className="px-8 py-6">
                  <form onSubmit={handleSaveProfile} className="space-y-6">
                    {/* All your existing form fields remain the same */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Username</label>
                        <input
                          type="text"
                          value={user.username}
                          disabled
                          className={`w-full p-3 border rounded-lg opacity-50 cursor-not-allowed ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white' 
                              : 'bg-gray-100 border-gray-300 text-gray-900'
                          }`}
                          placeholder="Username cannot be changed"
                        />
                        <p className="text-xs text-gray-500 mt-1">Username cannot be modified</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Email</label>
                        <input
                          type="email"
                          value={user.email || ''}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                          placeholder="Enter email"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">First Name</label>
                        <input
                          type="text"
                          value={user.firstName || ''}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                          placeholder="Enter first name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Last Name</label>
                        <input
                          type="text"
                          value={user.lastName || ''}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                          placeholder="Enter last name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Mobile Phone</label>
                        <input
                          type="tel"
                          value={user.mobilePhone || ''}
                          onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                          placeholder="Enter mobile phone"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Agency Name</label>
                        <select
                          value={user.agencyName || ''}
                          onChange={(e) => handleInputChange('agencyName', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                        >
                          <option value="">Select Agency</option>
                          <option value="GovTech">GovTech</option>
                          <option value="Gwinnett">Gwinnett</option>
                          <option value="COBB">COBB</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Jurisdiction Type</label>
                        <select
                          value={user.jurisdictionType || ''}
                          onChange={(e) => handleInputChange('jurisdictionType', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                        >
                          <option value="City">City</option>
                          <option value="County">County</option>
                          <option value="State">State</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Role at Agency</label>
                        <select
                          value={user.roleAtAgency || ''}
                          onChange={(e) => handleInputChange('roleAtAgency', e.target.value)}
                          className={`w-full p-3 border rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                        >
                          <option value="IT Administrator">IT Administrator</option>
                          <option value="Accela Developer">Accela Developer</option>
                          <option value="Accela Business Analyst">Accela Business Analyst</option>
                          <option value="Accela Quality Analyst">Accela Quality Analyst</option>
                        </select>
                      </div>
                    </div>

                    {/* Areas of Interest */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Areas of Interest</label>
                      <p className="text-sm text-gray-500 mb-4">Select your areas of interest to personalize your experience</p>
                      
                      {/* Multiselect Dropdown */}
                      <div className="dropdown-container relative">
                        <div 
                          className={`w-full min-h-[44px] px-4 py-3 border rounded-lg cursor-pointer flex flex-wrap items-center gap-2 transition-colors ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white hover:border-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                          } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                          onClick={() => setShowDropdown(!showDropdown)}
                        >
                          {selectedInterests.length === 0 ? (
                            <span className="text-gray-500">Select areas of interest...</span>
                          ) : (
                            selectedInterests.map((interest) => {
                              const option = interestOptions.find(opt => opt.value === interest);
                              return (
                                <span 
                                  key={interest}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-[#100E27] text-white text-sm rounded-full"
                                >
                                  {option?.label || interest}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInterestToggle(interest);
                                    }}
                                    className="ml-1 hover:bg-[#2a2a48] rounded-full w-4 h-4 flex items-center justify-center text-xs"
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })
                          )}
                          
                          {/* Dropdown Arrow */}
                          <svg 
                            className={`ml-auto w-5 h-5 transform transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>

                        {/* Dropdown Options */}
                        {showDropdown && (
                          <div className={`absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto border rounded-lg shadow-lg z-50 ${
                            isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                          }`}>
                            {interestOptions.map((option) => (
                              <div
                                key={option.value}
                                className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                } ${selectedInterests.includes(option.value) ? 
                                  (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : ''
                                }`}
                                onClick={() => handleInterestToggle(option.value)}
                              >
                                <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                                  selectedInterests.includes(option.value) 
                                    ? 'bg-[#100E27] border-[#100E27]' 
                                    : 'border-gray-300'
                                }`}>
                                  {selectedInterests.includes(option.value) && (
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <span className="text-sm">{option.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email Updates */}
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.emailUpdates || false}
                          onChange={(e) => handleInputChange('emailUpdates', e.target.checked)}
                          className="rounded text-[#100E27] focus:ring-[#100E27]"
                        />
                        <div>
                          <span className="font-medium">Subscribe for email updates</span>
                          <p className="text-sm text-gray-500">Receive notifications about new features, system updates, and important announcements</p>
                        </div>
                      </label>
                    </div>

                    {/* Security Section */}
                    <div>
                      <label className="block text-sm font-medium mb-4">Security</label>
                      <button
                        type="button"
                        onClick={() => setShowPasswordModal(true)}
                        className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                          isDarkMode 
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="16" r="1" fill="currentColor"/>
                          <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Change Password
                      </button>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end items-center pt-6 border-t border-gray-200">
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleCancel}
                          disabled={!isFormChanged() || isLoading}
                          className={`inline-flex items-center px-6 py-3 font-medium rounded-lg transition-all ${
                            isFormChanged() && !isLoading
                              ? 'bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300 hover:border-gray-400'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-200'
                          }`}
                        >
                          <svg width="16" height="16" className="mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={!isFormChanged() || isLoading}
                          className={`inline-flex items-center px-6 py-3 font-medium rounded-lg transition-all ${
                            isFormChanged() && !isLoading
                              ? 'bg-[#100E27] hover:bg-[#1a1a2e] text-white shadow-lg hover:shadow-xl'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg width="16" height="16" className="mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Password Change Modal - Same as before */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${
            isDarkMode ? 'bg-[#1F2023]' : 'bg-white'
          }`}>
            <div className={`flex justify-between items-center p-6 border-b ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordError('');
                  setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="p-6">
              {/* Error Message */}
              {passwordError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-red-600 text-sm">{passwordError}</span>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Current Password</label>
                  <input
                    type="password"
                    value={passwordFormData.currentPassword}
                    onChange={(e) => {
                      setPasswordFormData(prev => ({...prev, currentPassword: e.target.value}));
                      setPasswordError('');
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-transparent`}
                    required
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <input
                    type="password"
                    value={passwordFormData.newPassword}
                    onChange={(e) => {
                      setPasswordFormData(prev => ({...prev, newPassword: e.target.value}));
                      setPasswordError('');
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-transparent`}
                    required
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordFormData.confirmPassword}
                    onChange={(e) => {
                      setPasswordFormData(prev => ({...prev, confirmPassword: e.target.value}));
                      setPasswordError('');
                    }}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-transparent`}
                    required
                    placeholder="Confirm new password"
                  />
                </div>

                {/* Password Requirements */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <h4 className="text-sm font-medium mb-2">Password Requirements:</h4>
                  <ul className="text-xs space-y-1">
                    <li className={`flex items-center gap-2 ${
                      passwordFormData.newPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${
                        passwordFormData.newPassword.length >= 8 ? 'bg-green-600' : 'bg-gray-400'
                      }`}>
                        ✓
                      </span>
                      At least 8 characters long
                    </li>
                    <li className={`flex items-center gap-2 ${
                      /[A-Z]/.test(passwordFormData.newPassword) ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${
                        /[A-Z]/.test(passwordFormData.newPassword) ? 'bg-green-600' : 'bg-gray-400'
                      }`}>
                        ✓
                      </span>
                      At least one uppercase letter
                    </li>
                    <li className={`flex items-center gap-2 ${
                      /[a-z]/.test(passwordFormData.newPassword) ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${
                        /[a-z]/.test(passwordFormData.newPassword) ? 'bg-green-600' : 'bg-gray-400'
                      }`}>
                        ✓
                      </span>
                      At least one lowercase letter
                    </li>
                    <li className={`flex items-center gap-2 ${
                      /\d/.test(passwordFormData.newPassword) ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${
                        /\d/.test(passwordFormData.newPassword) ? 'bg-green-600' : 'bg-gray-400'
                      }`}>
                        ✓
                      </span>
                      At least one number
                    </li>
                    <li className={`flex items-center gap-2 ${
                      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passwordFormData.newPassword) ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${
                        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passwordFormData.newPassword) ? 'bg-green-600' : 'bg-gray-400'
                      }`}>
                        ✓
                      </span>
                      At least one special character
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError('');
                    setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!validatePassword(passwordFormData.newPassword) || 
                           passwordFormData.newPassword !== passwordFormData.confirmPassword ||
                           !passwordFormData.currentPassword}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    validatePassword(passwordFormData.newPassword) && 
                    passwordFormData.newPassword === passwordFormData.confirmPassword &&
                    passwordFormData.currentPassword
                      ? 'bg-[#100E27] text-white hover:bg-[#1a1a2e]'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;